import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const VALID_SLUGS = ["mentions-legales", "cgv", "confidentialite"];

// Composants markdown définis HORS du composant pour éviter de les recréer à chaque render
const MD_COMPONENTS = {
  h1: (props) => <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-6 mt-0" {...props} />,
  h2: (props) => <h2 className="text-xl font-bold tracking-tight mt-10 mb-3" {...props} />,
  h3: (props) => <h3 className="text-base font-bold mt-6 mb-2" {...props} />,
  p:  (props) => <p className="text-sm leading-7 text-foreground/80 mb-3" {...props} />,
  ul: (props) => <ul className="text-sm leading-7 list-disc pl-6 mb-3 text-foreground/80" {...props} />,
  ol: (props) => <ol className="text-sm leading-7 list-decimal pl-6 mb-3 text-foreground/80" {...props} />,
  li: (props) => <li className="mb-1" {...props} />,
  strong: (props) => <strong className="font-bold text-foreground" {...props} />,
  a: (props) => <a className="text-primary underline-offset-2 hover:underline" {...props} />,
  hr: () => <hr className="my-8 border-border" />,
  em: (props) => <em className="text-muted-foreground" {...props} />,
  code: (props) => <code className="bg-muted text-foreground rounded px-1 py-0.5 text-xs" {...props} />,
};

export default function LegalPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) {
      setError("Page inconnue.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setPage(null);
    api.get(`/public/legal/${slug}`)
      .then(({ data }) => {
        setPage(data);
        document.title = `${data.title} · WiLink`;
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`legal-page-${slug}`}>
      <PublicHeader />

      <section className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> Retour à l&apos;accueil
        </Link>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </div>
        )}

        {error && !loading && (
          <div className="bg-card border border-border rounded-md p-6 text-sm text-muted-foreground">{error}</div>
        )}

        {page && (
          <article className="prose prose-neutral dark:prose-invert max-w-none" data-testid="legal-content">
            <ReactMarkdown components={MD_COMPONENTS}>{page.content}</ReactMarkdown>
          </article>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
